import React, { useCallback, useRef, useState } from 'react';
import { ArrowUpRight, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { sellerSteps, contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import useReveal from '../hooks/useReveal';
import axios from 'axios';
import Turnstile, { HoneypotField, HONEYPOT_FIELD } from './Turnstile';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  'mt-2 w-full bg-transparent border-b border-white/15 focus:border-[color:var(--green)] py-3 text-lg outline-none transition-colors text-[color:var(--parchment)] placeholder:text-[color:var(--parchment)]/25';

const FieldTextarea = ({ field, value, onChange }) => (
  <textarea
    rows={4}
    value={value}
    onChange={onChange}
    placeholder={field.placeholder}
    className={`${inputClass} resize-none`}
  />
);

const FieldSelect = ({ field, value, onChange }) => (
  <select
    value={value}
    onChange={onChange}
    className={inputClass}
  >
    <option value="" className="bg-[color:var(--obsidian)]">Select an option …</option>
    {field.options.map((o) => (
      <option key={o} value={o} className="bg-[color:var(--obsidian)]">{o}</option>
    ))}
  </select>
);

const FieldInput = ({ field, value, onChange }) => (
  <input
    type={field.type}
    value={value}
    onChange={onChange}
    placeholder={field.placeholder}
    className={inputClass}
  />
);

const FIELD_COMPONENTS = {
  textarea: FieldTextarea,
  select: FieldSelect,
};

const Field = ({ field, value, onChange }) => {
  const Component = FIELD_COMPONENTS[field.type] || FieldInput;
  return (
    <div>
      <label className="font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]/60">
        {field.label}{field.required ? '*' : ''}
      </label>
      <Component field={field} value={value} onChange={onChange} />
    </div>
  );
};

const getStepMarkerClass = (index, currentStep, isDone) => {
  if (isDone || currentStep > index) {
    return 'bg-[color:var(--green)] border-[color:var(--green)] text-[color:var(--obsidian)]';
  }
  if (currentStep === index) {
    return 'border-[color:var(--green)] text-[color:var(--green)]';
  }
  return 'border-white/15 text-[color:var(--parchment)]/50';
};

const StepIndicator = ({ step, done }) => (
  <div className="reveal mt-10 space-y-4">
    {sellerSteps.map((s, i) => {
      const isComplete = done || step > i;
      const markerClass = getStepMarkerClass(i, step, done);
      const kickerClass = step === i && !done
        ? 'text-[color:var(--green)]'
        : 'text-[color:var(--parchment)]/55';
      return (
        <div key={s.id} className="flex items-start gap-4">
          <div className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center font-mono text-[11px] transition-all duration-500 ${markerClass}`}>
            {isComplete ? <Check className="w-4 h-4" /> : String(s.id).padStart(2, '0')}
          </div>
          <div>
            <div className={`font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase ${kickerClass}`}>Step {s.id}</div>
            <div className="font-display text-lg text-[color:var(--parchment)] leading-tight mt-0.5">{s.label}</div>
          </div>
        </div>
      );
    })}
  </div>
);

const StepProgress = ({ step }) => (
  <div className="flex items-center gap-1">
    {sellerSteps.map((s, i) => (
      <div
        key={s.id}
        className={`h-0.5 rounded-full transition-all duration-500 ${i <= step ? 'w-10 bg-[color:var(--green)]' : 'w-6 bg-white/15'}`}
      />
    ))}
  </div>
);

const StepForm = ({ current, step, data, submitting, onFieldChange, onBack, onNext, isLast, hp, setHp, turnstileRef, setToken, token }) => (
  <>
    <div className="flex items-center justify-between mb-8">
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/50">
        Step {current.id} · of {sellerSteps.length}
      </div>
      <StepProgress step={step} />
    </div>

    <h3 className="font-display text-[26px] md:text-[36px] leading-[1.1] text-[color:var(--parchment)]">{current.label}</h3>
    <p className="mt-3 text-[color:var(--parchment)]/65 max-w-lg">{current.prompt}</p>

    <div className="mt-10 space-y-8">
      {current.fields.map((f) => (
        <Field
          key={f.name}
          field={f}
          value={data[f.name] || ''}
          onChange={(e) => onFieldChange(f.name, e.target.value)}
        />
      ))}
    </div>

    {isLast && (
      <>
        <HoneypotField value={hp} onChange={setHp} />
        <div className="mt-10">
          <Turnstile ref={turnstileRef} onToken={setToken} theme="dark" />
        </div>
      </>
    )}

    <div className="mt-12 flex items-center justify-between">
      <button
        onClick={onBack}
        disabled={step === 0}
        className={`inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.14em] ${step === 0 ? 'opacity-30 cursor-not-allowed' : 'text-[color:var(--parchment)] hover:text-[color:var(--green)]'}`}
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <button onClick={onNext} disabled={submitting || (isLast && !token)} className={`btn-primary ${(submitting || (isLast && !token)) ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid="seller-portal-next-btn">
        {submitting
          ? 'Submitting…'
          : isLast ? 'Submit Request' : 'Continue'}
        {!submitting && <ArrowRight className="w-3.5 h-3.5" />}
      </button>
    </div>
  </>
);

const Confirmation = ({ name, onRestart }) => {
  const firstName = (name || '').split(' ')[0] || 'friend';
  return (
    <div className="py-6">
      <div className="w-14 h-14 rounded-full bg-[color:var(--green)] flex items-center justify-center mb-6">
        <Check className="w-6 h-6 text-[color:var(--obsidian)]" />
      </div>
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-3">Request Received</div>
      <h3 className="font-display text-[32px] md:text-[44px] leading-[1.05] text-[color:var(--parchment)]">
        Thank you, {firstName}.
      </h3>
      <p className="mt-4 text-[color:var(--parchment)]/70 max-w-md">
        A specialist will be in touch within one business day. In the meantime, feel free to browse <a href="#vault" className="underline text-[color:var(--green)]">The Vault</a>.
      </p>
      <button onClick={onRestart} className="mt-8 inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)] hover:text-[color:var(--green)]">
        Submit another request <ArrowUpRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const isStepComplete = (step, data) =>
  step.fields.every((f) => (f.required ? !!data[f.name] : true));

const SellerPortal = () => {
  const sectionRef = useReveal({ threshold: 0.1 });
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState('');
  const [hp, setHp] = useState('');
  const turnstileRef = useRef(null);
  const setTokenStable = useCallback((v) => setToken(v), []);
  const { toast } = useToast();

  const current = sellerSteps[step];
  const isLast = step === sellerSteps.length - 1;

  const handleFieldChange = (name, value) => setData({ ...data, [name]: value });

  const handleNext = async () => {
    if (!isStepComplete(current, data)) {
      toast({ title: 'A required field is empty', description: 'Please complete the required entries to continue.' });
      return;
    }
    if (isLast) {
      if (!token) {
        toast({ title: 'Security check needed', description: 'Please complete the security check before submitting.' });
        return;
      }
      setSubmitting(true);
      try {
        await axios.post(`${API_BASE}/api/forms/need-our-help`, {
          ...data,
          [HONEYPOT_FIELD]: hp,
          turnstile_token: token,
        });
        setDone(true);
        toast({ title: 'Received', description: 'A specialist will reach out within one business day.' });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast({
          title: 'Submission failed',
          description: typeof detail === 'string' ? detail : 'Please try again in a moment or call us directly.',
        });
      } finally {
        turnstileRef.current?.reset();
        setToken('');
        setSubmitting(false);
      }
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => setStep(Math.max(0, step - 1));
  const handleRestart = () => {
    setStep(0); setData({}); setDone(false); setToken(''); setHp('');
    turnstileRef.current?.reset();
  };

  return (
    <section id="sell" ref={sectionRef} className="relative py-16 md:py-20 px-6 md:px-10 border-t border-white/5 overflow-hidden">
      {/* Atmospheric loft background — darkened for text/form legibility */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <img
          src="/sell-loft.webp" srcSet="/sell-loft-400w.webp 400w, /sell-loft-800w.webp 800w, /sell-loft.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
          alt=""
          className="w-full h-full object-cover object-center opacity-[0.55]"
        />
        <div className="absolute inset-0 bg-[color:var(--obsidian)]/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--obsidian)] via-transparent to-[color:var(--obsidian)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,10,10,0.35)_65%,rgba(10,10,10,0.85)_100%)]" />
      </div>

      <div className="pointer-events-none absolute -right-24 top-40 w-[420px] h-[420px] rounded-full bg-[color:var(--green)]/[0.08] blur-3xl animate-floaty-slow" />

      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-[.42fr_.58fr] gap-12">
        <div>
          <div className="reveal font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">
            &mdash; Need Our Help &middot; Three Steps
          </div>
          <h2 className="reveal font-display text-[38px] md:text-[62px] leading-[1.02] text-[color:var(--parchment)]">
            Tell us about your <span className="text-[color:var(--green)]">estate</span>.
          </h2>
          <p className="reveal mt-6 text-[color:var(--parchment)]/70 leading-relaxed max-w-md">
            A quick three-step intake. Share what you know about the property, your goals, and how to reach you. A specialist will follow up &mdash; no obligation, no pressure.
          </p>

          <StepIndicator step={step} done={done} />

          <div className="reveal mt-10 p-5 border border-white/10 rounded-xl">
            <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]/50">Or reach us directly</div>
            <a href={contactInfo.phoneHref} className="block mt-2 font-display text-2xl text-[color:var(--parchment)] hover:text-[color:var(--green)] transition-colors">{contactInfo.phone}</a>
          </div>
        </div>

        <div className="reveal relative rounded-[6px] bg-[color:var(--obsidian-2)]/70 backdrop-blur-md border border-white/10 p-8 md:p-12 shadow-2xl shadow-black/50">
          {done
            ? <Confirmation name={data.name} onRestart={handleRestart} />
            : (
              <StepForm
                current={current}
                step={step}
                data={data}
                submitting={submitting}
                onFieldChange={handleFieldChange}
                onBack={handleBack}
                onNext={handleNext}
                isLast={isLast}
                hp={hp}
                setHp={setHp}
                turnstileRef={turnstileRef}
                setToken={setTokenStable}
                token={token}
              />
            )
          }
        </div>
      </div>
    </section>
  );
};

export default SellerPortal;
